import type { CerFeatures } from '@slims/shared/data';

import { useMemo, useState } from 'react';
import type { ScopeLeaf } from '@/hooks/use-scope-tree';
import { useSelectedScopes } from '@/hooks/use-selected-scopes';

export type ConfigViewMode = 'multi' | 'single';

export interface ConfigToggleItem {
	id: string;
	cerNome: string;
	configNome: string;
}

/** Le sole prop del toggle Panoramica/singola: vuoto con una configurazione sola. */
export interface ConfigToggleProps {
	view?: ConfigViewMode;
	onViewChange?: (view: ConfigViewMode) => void;
	configs?: ConfigToggleItem[];
	selectedConfigId?: string;
	onSelectConfig?: (orgId: string) => void;
}

/**
 * Il toggle «Panoramica»/«Configurazione singola» delle pagine che guardano le
 * configurazioni scelte nel selettore dell'header (Dashboard, Monitoraggio,
 * Previsione, Incentivi, Anomalie, Report). Era copiato in ognuna.
 *
 * `effectiveLeaf` è la configurazione aperta: la prima in ambito finché
 * l'utente non ne sceglie un'altra (picker o clic su una card), e se la
 * selezione dell'header esclude quella aperta si ricade da sé sulla prima
 * rimasta. È `undefined` solo quando non c'è nessuna configurazione in ambito.
 */
export function useConfigToggle(feature?: keyof CerFeatures) {
	const { scopes, isMulti, loading } = useSelectedScopes(feature);
	const [view, setView] = useState<ConfigViewMode>('multi');
	const [selectedConfigId, setSelectedConfigId] = useState<string | null>(null);

	const configs = useMemo<ConfigToggleItem[]>(
		() =>
			scopes.map((leaf) => ({
				id: leaf.orgId,
				cerNome: leaf.cerNome,
				configNome: leaf.configNome,
			})),
		[scopes],
	);

	function openConfig(orgId: string) {
		setSelectedConfigId(orgId);
		setView('single');
	}

	const effectiveLeaf: ScopeLeaf | undefined =
		scopes.find((leaf) => leaf.orgId === selectedConfigId) ?? scopes[0];

	const toggle: ConfigToggleProps =
		isMulti && effectiveLeaf
			? {
					view,
					onViewChange: setView,
					configs,
					selectedConfigId: effectiveLeaf.orgId,
					onSelectConfig: openConfig,
				}
			: {};

	return {
		scopes,
		isMulti,
		loading,
		view,
		setView,
		configs,
		effectiveLeaf,
		openConfig,
		toggle,
		/** Con più configurazioni in ambito e la Panoramica scelta. */
		showOverview: isMulti && view === 'multi',
	};
}
